/**
 * inlineEditDiff — governed inline cell edits for the CRM grid.
 *
 * WHY this exists: the auditor flagged that inline edits bypassed the
 * approval queue and the ledger — a cell edit mutated the store
 * directly with no old→new diff and no governance. This module is the
 * PURE core that closes that gap: every cell edit is first compiled
 * into an `ActionDiff` (the same old→new trust mechanic
 * src/lib/agent/actionDiff.ts renders on confirm cards), batched per
 * record, and routed by {@link commitPlan} into either the auto-apply
 * lane or the approval queue — NEVER a direct store write from here.
 *
 * Design contract:
 * - PURE: no store access, no IO. `commitPlan` deliberately accepts an
 *   (optional, injectable) store handle and never calls it — tests
 *   assert this with a spy, so a future regression that mutates the
 *   store from the planner fails loudly.
 * - Trivial edits (value unchanged) are dropped before they ever reach
 *   a queue: a no-op must not spend operator attention.
 * - The actual writes stay with the existing seals: auto-apply lanes
 *   call the typed db adapters at the call site, queued lanes enter
 *   queueApproval → resolveApproval like every other agent proposal.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One inline cell edit: which record, which attr, old → new. */
export interface CellEdit {
  recordId: string;
  attr: string;
  from: unknown;
  to: unknown;
}

/** One field change inside an {@link ActionDiff}. */
export interface FieldChange {
  attr: string;
  from: unknown;
  to: unknown;
}

/**
 * The old→new diff for one record — same shape of story
 * `diffForAction` tells on approval cards: every change carries its
 * prior value, and `summary` is the render-ready "attr: from → to"
 * line(s).
 */
export interface ActionDiff {
  recordId: string;
  changes: FieldChange[];
  summary: string;
}

// ---------------------------------------------------------------------------
// Diff building
// ---------------------------------------------------------------------------

/** Render one value for a summary line — honest '—' for empty. */
function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  const s = typeof v === 'string' ? v : String(v);
  return s.trim() === '' ? '—' : s;
}

/** The "attr: from → to" line (or "attr → to" when there was no prior). */
function changeLine(c: FieldChange): string {
  return show(c.from) === '—'
    ? `${c.attr} → ${show(c.to)}`
    : `${c.attr}: ${show(c.from)} → ${show(c.to)}`;
}

/** Compile one cell edit into its old→new ActionDiff. Pure. */
export function buildCellEditDiff(edit: CellEdit): ActionDiff {
  const change: FieldChange = { attr: edit.attr, from: edit.from, to: edit.to };
  return {
    recordId: edit.recordId,
    changes: [change],
    summary: changeLine(change),
  };
}

/**
 * A trivial edit changes nothing: strictly equal values, or both
 * "empty" (null/undefined/blank string) — clearing an already-blank
 * cell is not a change worth queueing.
 */
export function isTrivialEdit(edit: CellEdit): boolean {
  if (edit.from === edit.to) return true;
  const empty = (v: unknown) =>
    v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
  return empty(edit.from) && empty(edit.to);
}

/**
 * Batch raw cell edits into per-record diffs: trivial edits are
 * dropped, edits to the same record group into ONE ActionDiff (in
 * first-seen record order), and a later edit to the same attr on the
 * same record supersedes the earlier one — the operator's last
 * keystroke wins, with the ORIGINAL `from` preserved so the diff still
 * tells the true old→new story. Pure.
 */
export function batchCellEdits(edits: CellEdit[]): ActionDiff[] {
  const byRecord = new Map<string, FieldChange[]>();
  for (const edit of edits) {
    if (isTrivialEdit(edit)) continue;
    const changes = byRecord.get(edit.recordId) ?? [];
    const prior = changes.find((c) => c.attr === edit.attr);
    if (prior) {
      prior.to = edit.to; // last write wins; keep the original `from`
    } else {
      changes.push({ attr: edit.attr, from: edit.from, to: edit.to });
    }
    byRecord.set(edit.recordId, changes);
  }
  const out: ActionDiff[] = [];
  for (const [recordId, changes] of byRecord) {
    // Superseded pairs can net out to a no-op (A→B then B→A): drop them.
    const real = changes.filter((c) => !isTrivialEdit({ recordId, ...c }));
    if (real.length === 0) continue;
    out.push({ recordId, changes: real, summary: real.map(changeLine).join('; ') });
  }
  return out;
}

// ---------------------------------------------------------------------------
// commitPlan — the governance router
// ---------------------------------------------------------------------------

export interface CommitPlanOptions {
  /** true → every diff enters the approval queue; false → auto-apply lane. */
  requireApproval: boolean;
  /**
   * Injectable store handle. The planner NEVER calls it — it exists so
   * tests can prove (with a spy) that planning is write-free. Writes
   * happen downstream: auto-apply via the typed db adapters, queued
   * via queueApproval → resolveApproval.
   */
  store?: { update: (...args: unknown[]) => unknown };
}

/** The routed plan: what applies now vs what waits for sign-off. */
export interface CommitPlan {
  autoApply: ActionDiff[];
  queued: ActionDiff[];
}

/**
 * Route batched edits through the approval-queue decision WITHOUT
 * touching the store: with `requireApproval` every diff is queued for
 * the operator; without it every diff lands in the auto-apply lane for
 * the caller to execute through the existing typed adapters (which own
 * audit stamping). Trivial edits never survive into either lane. Pure.
 */
export function commitPlan(edits: CellEdit[], opts: CommitPlanOptions): CommitPlan {
  const diffs = batchCellEdits(edits);
  return opts.requireApproval
    ? { autoApply: [], queued: diffs }
    : { autoApply: diffs, queued: [] };
}
